import React, { useState, useEffect, useMemo } from 'react';
import {
    Card,
    Button,
    Space,
    message,
    Tag,
    Row,
    Col,
    Statistic,
    Tabs,
    Spin,
    Table,
    Empty,
    Tooltip,
    Typography,
    DatePicker
} from 'antd';
import {
    ReloadOutlined,
    DollarOutlined,
    NumberOutlined,
    PercentageOutlined,
    TableOutlined,
    ExportOutlined
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useAuth } from '../../../hooks/useAuth';
import settlementApi from '../../../api/settlementApi';
import { getFlightHoursTimeSeries, getAggregatedMetrics, getAggregatedMetricsRange, getMonthlyBhFhSeries, getYearlyBhFhSeries, getRouteAvgBh, getRouteAvgBhDetail, getLegStats, getTaxiTimeSeries } from '../../../api/flightOperationStatsApi';
import AircraftSelector from '../../../components/common/AircraftSelector';
import { BhFhChartRow } from '../shared/charts/BhFhTimeSeriesChart';
import { TaxiChartRow } from '../shared/charts/TaxiTimeSeriesChart';
import MonthlyBhFhChart from '../shared/charts/MonthlyBhFhChart';
import YearlyBhFhChart from '../shared/charts/YearlyBhFhChart';
import AirportFeesPanel from './components/AirportFeesPanel';
import FuelCoveragePanel from './components/FuelCoveragePanel';
import AirportFuelPricePanel from './components/AirportFuelPricePanel';
import { exportDataToExcel } from '../../../utils/excelUtils';
import './StatisticsDashboard.css';

const { Text } = Typography;
const { RangePicker } = DatePicker;

// 看板全部可能的顶层 TAB key（实际可见性还受权限过滤，见 getTabItems / resolvedActiveTab）
const ALL_TAB_KEYS = ['bh_fh', 'fuel', 'airport_fuel_price', 'deicing', 'ground_handling', 'landing_parking', 'navigation'];
const DEFAULT_TAB_KEY = 'bh_fh';

const INITIAL_LAZY_LOADED = {
    bhFhTimeSeries: false,
    aggregatedMetrics: false,
    routeAvg: false,
    monthlySeries: false,
    yearlySeries: false,
    legStats: false,
    taxiTimeSeries: false,
};

/**
 * 统计报表页面（完整版）
 * 从 profit-reports 的统计看板Tab提取的完整内容
 */
const StatisticsDashboardPage = () => {
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    // TAB 与地址栏 ?tab= 双向同步（参照 LegCostHubPage）：activeTab 从 URL 派生，
    // 不再用 useState——避免两套状态互相覆盖；合法性校验与回退见 resolvedActiveTab
    const [searchParams, setSearchParams] = useSearchParams();
    
    // 数据状态
    const [deicingStats, setDeicingStats] = useState(null);
    const [bhFhStats, setBhFhStats] = useState(null);
    const [bhFhTimeSeriesData, setBhFhTimeSeriesData] = useState({});
    const [bhFhTimeSeriesLoading, setBhFhTimeSeriesLoading] = useState(false);
    const [airportFeesStats, setAirportFeesStats] = useState(null);
    
    // BH/FH Tab新增状态
    const [aggregatedMetrics, setAggregatedMetrics] = useState({});
    const [aggregatedMetricsLoading, setAggregatedMetricsLoading] = useState(false);
    const [monthlySeriesData, setMonthlySeriesData] = useState(null);
    const [monthlySeriesLoading, setMonthlySeriesLoading] = useState(false);
    const [yearlySeriesData, setYearlySeriesData] = useState(null);
    const [yearlySeriesLoading, setYearlySeriesLoading] = useState(false);
    const [routeAvgData, setRouteAvgData] = useState([]);
    const [routeAvgLoading, setRouteAvgLoading] = useState(false);
    const [legStatsData, setLegStatsData] = useState([]);
    const [legStatsLoading, setLegStatsLoading] = useState(false);
    const [taxiTimeSeriesData, setTaxiTimeSeriesData] = useState({});
    const [taxiTimeSeriesLoading, setTaxiTimeSeriesLoading] = useState(false);
    
    // 航线平均BH钻取明细状态
    const [routeDetailMap, setRouteDetailMap] = useState({});
    const [routeDetailLoadingKeys, setRouteDetailLoadingKeys] = useState({});
    const [appliedDateRange, setAppliedDateRange] = useState(null);

    // UI状态
    const [loading, setLoading] = useState(false);
    const tabFromUrl = searchParams.get('tab');
    // 空 / 非法值归一化为默认 TAB（与原 useState('bh_fh') 行为一致）；权限过滤后的回退见 resolvedActiveTab
    const activeTab = ALL_TAB_KEYS.includes(tabFromUrl) ? tabFromUrl : DEFAULT_TAB_KEY;
    const [bhFhChartTab, setBhFhChartTab] = useState('timeseries');
    const [lazyLoaded, setLazyLoaded] = useState(INITIAL_LAZY_LOADED);
    const [dashboardData, setDashboardData] = useState({});
    const [bhFhAircraftList, setBhFhAircraftList] = useState([]);
    
    // 默认日期范围：当前月份
    const getDefaultDateRange = () => {
        const now = dayjs();
        return [
            now.startOf('month').format('YYYY-MM-DD'),
            now.endOf('month').format('YYYY-MM-DD')
        ];
    };
    
    // 筛选条件
    const [filters, setFilters] = useState({
        aircraft_reg: [],
        date_range: getDefaultDateRange(),
        complete_only: true
    });

    // 页面加载时获取数据
    useEffect(() => {
        fetchDashboardStatistics();
    }, []);

    // 计算时间粒度
    const calculateGranularity = (startDate, endDate) => {
        if (!startDate || !endDate) return 'day';
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
        if (diffDays <= 30) return 'day';
        if (diffDays <= 90) return 'week';
        return 'month';
    };

    // 获取BH/FH时间序列数据（使用UTC日拆分API）
    // 参数可选：传入aircraftRegs则按指定飞机查；不传则查所有有数据的飞机
    // 返回从API响应中提取的飞机注册号列表
    const fetchBhFhTimeSeriesData = async (aircraftRegs) => {
        setBhFhTimeSeriesLoading(true);
        try {
            const params = {
                start_date: filters.date_range[0],
                end_date: filters.date_range[1],
            };
            if (aircraftRegs?.length > 0) {
                params.aircraft_reg = aircraftRegs.join(',');
            }
            const response = await getFlightHoursTimeSeries(params);

            // 将新API的items格式转换为图表组件需要的 {dates, fh_values, bh_values} 格式
            const grouped = {};
            (response?.items || []).forEach(item => {
                if (!grouped[item.aircraft_reg]) {
                    grouped[item.aircraft_reg] = {};
                }
                grouped[item.aircraft_reg][item.flight_date] = item;
            });

            const timeSeriesData = {};
            Object.keys(grouped).forEach(reg => {
                const regData = grouped[reg];
                const dates = Object.keys(regData).sort();
                timeSeriesData[reg] = {
                    dates,
                    bh_values: dates.map(d => {
                        const item = regData[d];
                        const legs = item?.leg_count || 0;
                        return legs > 0 ? Number(((item?.bh ?? 0) / legs).toFixed(2)) : 0;
                    }),
                    fh_values: dates.map(d => {
                        const item = regData[d];
                        const legs = item?.leg_count || 0;
                        return legs > 0 ? Number(((item?.fh ?? 0) / legs).toFixed(2)) : 0;
                    }),
                    leg_counts: dates.map(d => regData[d]?.leg_count ?? 0),
                };
            });
            setBhFhTimeSeriesData(timeSeriesData);
            // 返回有数据的飞机列表
            return Object.keys(grouped).map(reg => ({ aircraft_reg: reg }));
        } catch (error) {
            console.error('获取BH/FH时间序列失败:', error);
            return [];
        } finally {
            setBhFhTimeSeriesLoading(false);
        }
    };

    // 加载指定航段的燃油时间序列
    // 获取燃油时间序列数据（初始加载 TOP 5）
    const fetchAggregatedMetrics = async (aircraftList) => {
        setAggregatedMetricsLoading(true);
        try {
            const dateRange = filters.date_range;
            if (!dateRange || dateRange.length !== 2) return;
            const startDate = dayjs(dateRange[0]);
            const endDate = dayjs(dateRange[1]);
            const crossMonth = !startDate.isSame(endDate, 'month');

            const apiFn = crossMonth
                ? (aircraft) => getAggregatedMetricsRange({
                    aircraft_reg: aircraft.aircraft_reg,
                    start_date: startDate.format('YYYY-MM-DD'),
                    end_date: endDate.format('YYYY-MM-DD'),
                })
                : (aircraft) => getAggregatedMetrics({
                    aircraft_reg: aircraft.aircraft_reg,
                    year: startDate.year(),
                    month: startDate.month() + 1,
                });

            const requests = aircraftList.map(aircraft =>
                apiFn(aircraft).catch(err => {
                    console.error(`获取${aircraft.aircraft_reg}聚合指标失败:`, err);
                    return null;
                })
            );
            const responses = await Promise.all(requests);
            const results = {};
            for (const res of responses) {
                if (res?.aircraft_reg) results[res.aircraft_reg] = res;
            }

            setAggregatedMetrics(results);
        } catch (error) {
            console.error('获取聚合指标失败:', error);
        } finally {
            setAggregatedMetricsLoading(false);
        }
    };

    // 获取月度BH/FH序列（跟随页面日期筛选器：覆盖整月，可跨年）
    const fetchMonthlySeriesData = async (aircraftList) => {
        setMonthlySeriesLoading(true);
        try {
            const dateRange = filters.date_range;
            if (!dateRange || dateRange.length !== 2) return;
            const regs = aircraftList.map(a => a.aircraft_reg).join(',');
            const result = await getMonthlyBhFhSeries({
                aircraft_reg: regs,
                start_date: dateRange[0],
                end_date: dateRange[1],
            });
            setMonthlySeriesData(result);
        } catch (error) {
            console.error('获取月度BH/FH序列失败:', error);
        } finally {
            setMonthlySeriesLoading(false);
        }
    };

    // 获取年度BH/FH序列（所有年份，不受筛选器影响）
    const fetchYearlySeriesData = async (aircraftList) => {
        setYearlySeriesLoading(true);
        try {
            const regs = aircraftList.map(a => a.aircraft_reg).join(',');
            const result = await getYearlyBhFhSeries({ aircraft_reg: regs });
            setYearlySeriesData(result);
        } catch (error) {
            console.error('获取年度BH/FH序列失败:', error);
        } finally {
            setYearlySeriesLoading(false);
        }
    };

    // 获取按航线统计平均BH（独立数据源）
    const fetchRouteAvgData = async (aircraftList) => {
        setRouteAvgLoading(true);
        try {
            const regs = aircraftList.map(a => a.aircraft_reg).join(',');
            const dateRange = filters.date_range;
            if (!dateRange || dateRange.length !== 2) return;
            const result = await getRouteAvgBh({ aircraft_reg: regs, start_date: dateRange[0], end_date: dateRange[1] });
            setRouteAvgData(result || []);
        } catch (error) {
            console.error('获取按航线统计平均BH失败:', error);
        } finally {
            setRouteAvgLoading(false);
        }
    };

    // 航线平均BH钻取明细：懒加载（使用 appliedDateRange 确保参数与表格主数据一致）
    const handleRouteAvgExpand = async (expanded, record) => {
        const key = `${record.aircraft_reg}_${record.route_code}`;
        if (!expanded || routeDetailMap[key]) return;

        setRouteDetailLoadingKeys(prev => ({ ...prev, [key]: true }));
        try {
            const detail = await getRouteAvgBhDetail({
                aircraft_reg: record.aircraft_reg,
                route_code: record.route_code,
                start_date: appliedDateRange?.[0],
                end_date: appliedDateRange?.[1],
            });
            setRouteDetailMap(prev => ({ ...prev, [key]: detail }));
        } catch (error) {
            console.error('获取航线平均BH钻取明细失败:', error);
            message.error('加载明细失败');
        } finally {
            setRouteDetailLoadingKeys(prev => {
                const { [key]: _, ...rest } = prev;
                return rest;
            });
        }
    };

    // 航段明细表格列定义（参照 profit-reports 的 getLegDetailColumns）
    const routeDetailLegColumns = [
        {
            title: '段次',
            dataIndex: 'sequence',
            key: 'sequence',
            width: 70,
            align: 'center',
            render: (seq) => <Tag color="blue">第 {seq} 段</Tag>
        },
        {
            title: '航班日期',
            dataIndex: 'flight_date',
            key: 'flight_date',
            width: 120,
            align: 'center',
            render: (text) => {
                if (!text) return <Text style={{ fontSize: 13 }}>-</Text>;
                const d = dayjs(text);
                return <Text style={{ fontSize: 13 }}>{d.isValid() ? d.format('YYYY-MM-DD') : text}</Text>;
            }
        },
        {
            title: '航班号',
            dataIndex: 'flight_number',
            key: 'flight_number',
            width: 110,
            align: 'center',
            render: (text) => <Text style={{ fontWeight: 500, fontSize: 13 }}>{text || '-'}</Text>
        },
        {
            title: '起飞机场',
            dataIndex: 'departure_airport',
            key: 'departure_airport',
            width: 100,
            align: 'center',
            render: (text) => <Text strong style={{ color: '#1890ff', fontSize: 13 }}>{text}</Text>
        },
        {
            title: '到达机场',
            dataIndex: 'arrival_airport',
            key: 'arrival_airport',
            width: 100,
            align: 'center',
            render: (text) => <Text strong style={{ color: '#1890ff', fontSize: 13 }}>{text}</Text>
        },
        {
            title: 'BH (小时)',
            dataIndex: 'bh',
            key: 'bh',
            width: 110,
            align: 'right',
            render: (value) => (
                <Text strong style={{ color: '#722ed1', fontSize: 14 }}>
                    {value != null ? `${Number(value).toFixed(2)}H` : '-'}
                </Text>
            )
        },
    ];

    // 航线平均BH钻取明细：展开区域渲染（卡片头部 + 表格明细）
    const renderRouteAvgExpandedRow = (record) => {
        const key = `${record.aircraft_reg}_${record.route_code}`;
        const isLoading = !!routeDetailLoadingKeys[key];
        const detail = routeDetailMap[key];

        if (isLoading) {
            return <div style={{ textAlign: 'center', padding: '24px' }}><Spin tip="加载航线实例明细..." /></div>;
        }

        if (!detail || !detail.instances || detail.instances.length === 0) {
            return <Empty description="暂无实例明细" style={{ padding: '16px' }} />;
        }

        return (
            <div className="route-detail-expand-area">
                {detail.instances.map((inst) => (
                    <div key={inst.instance_index} className={`route-instance-card ${inst.instance_index % 2 === 0 ? 'route-instance-card-alt' : ''}`}>
                        <div className="route-instance-card-header">
                            <Space size={12}>
                                <Tag color="blue" style={{ margin: 0, fontWeight: 600 }}>第{inst.instance_index}次</Tag>
                                <Text type="secondary" style={{ fontSize: 13 }}>{inst.first_flight_date || '-'}</Text>
                                {inst.legs?.[0]?.flight_number && (
                                    <Text style={{ fontSize: 13, color: '#555' }}>{inst.legs[0].flight_number}</Text>
                                )}
                            </Space>
                            <Text strong style={{ fontSize: 16, color: '#722ed1' }}>
                                总BH: {inst.total_bh?.toFixed(2)} h
                            </Text>
                        </div>
                        <div className="route-instance-card-body">
                            <Table
                                dataSource={inst.legs || []}
                                columns={routeDetailLegColumns}
                                rowKey="sequence"
                                pagination={false}
                                size="small"
                                bordered
                                style={{ borderRadius: 4 }}
                            />
                        </div>
                    </div>
                ))}
                <div className="route-detail-summary">
                    <Text type="secondary" style={{ fontSize: 13 }}>
                        共 {detail.route_instance_count} 次飞行
                    </Text>
                    <Text strong style={{ fontSize: 14, color: '#722ed1' }}>
                        平均BH: {detail.avg_bh?.toFixed(2)} h
                    </Text>
                </div>
            </div>
        );
    };

    // 获取航段统计（独立数据源）
    const fetchLegStatsData = async (aircraftList) => {
        setLegStatsLoading(true);
        try {
            const regs = aircraftList.map(a => a.aircraft_reg).join(',');
            const dateRange = filters.date_range;
            if (!dateRange || dateRange.length !== 2) return;
            const result = await getLegStats({ aircraft_reg: regs, start_date: dateRange[0], end_date: dateRange[1] });
            setLegStatsData(result || []);
        } catch (error) {
            console.error('获取航段统计失败:', error);
        } finally {
            setLegStatsLoading(false);
        }
    };

    const fetchTaxiTimeSeriesData = async (aircraftList) => {
        setTaxiTimeSeriesLoading(true);
        try {
            const dateRange = filters.date_range;
            if (!dateRange || dateRange.length !== 2) return;
            const granularity = calculateGranularity(dateRange[0], dateRange[1]);
            const params = {
                start_date: dateRange[0],
                end_date: dateRange[1],
                granularity,
            };
            if (aircraftList?.length > 0) {
                params.aircraft_reg = aircraftList.map(a => a.aircraft_reg).join(',');
            }
            const result = await getTaxiTimeSeries(params);
            const grouped = {};
            (result || []).forEach(item => {
                if (item.data && item.data.dates && item.data.dates.length > 0) {
                    grouped[item.airport] = item.data;
                }
            });
            setTaxiTimeSeriesData(grouped);
        } catch (error) {
            console.error('获取滑行时间序列失败:', error);
        } finally {
            setTaxiTimeSeriesLoading(false);
        }
    };

    // 获取统计看板所有数据
    const markLazyLoaded = (key) => {
        setLazyLoaded(prev => ({ ...prev, [key]: true }));
    };

    const resetLazyData = ({ clearSummaryData = true } = {}) => {
        setLazyLoaded({ ...INITIAL_LAZY_LOADED });
        setBhFhAircraftList([]);
        setBhFhTimeSeriesData({});
        setAggregatedMetrics({});
        setMonthlySeriesData(null);
        setYearlySeriesData(null);
        setRouteAvgData([]);
        setLegStatsData([]);
        setTaxiTimeSeriesData({});

        if (clearSummaryData) {
            setDashboardData({});
            setDeicingStats(null);
            setBhFhStats(null);
            setAirportFeesStats(null);
        }
    };

    const resolveBhFhAircraftList = async (data) => {
        const userFilterRegs = Array.isArray(filters.aircraft_reg) && filters.aircraft_reg.length > 0
            ? filters.aircraft_reg : null;
        const profitAircraftList = data?.bh_fh?.by_aircraft || [];
        const regsFromProfit = profitAircraftList.map(a => a.aircraft_reg).filter(Boolean);
        const timeSeriesRegs = userFilterRegs || (regsFromProfit.length > 0 ? regsFromProfit : undefined);
        const timeSeriesAircraftList = await fetchBhFhTimeSeriesData(timeSeriesRegs);
        markLazyLoaded('bhFhTimeSeries');

        const allRegs = new Set(regsFromProfit);
        timeSeriesAircraftList.forEach(a => {
            if (a?.aircraft_reg) allRegs.add(a.aircraft_reg);
        });

        const aircraftList = userFilterRegs
            ? Array.from(allRegs).filter(reg => userFilterRegs.includes(reg)).map(reg => ({ aircraft_reg: reg }))
            : Array.from(allRegs).map(reg => ({ aircraft_reg: reg }));

        setBhFhAircraftList(aircraftList);
        return aircraftList;
    };

    const loadBhFhBaseData = async (data) => {
        const aircraftList = await resolveBhFhAircraftList(data);
        if (aircraftList.length === 0) return aircraftList;

        await Promise.all([
            fetchAggregatedMetrics(aircraftList).then(() => markLazyLoaded('aggregatedMetrics')),
            fetchRouteAvgData(aircraftList).then(() => markLazyLoaded('routeAvg')),
        ]);
        return aircraftList;
    };

    const loadBhFhChartData = async (tabKey, aircraftList = bhFhAircraftList, force = false) => {
        if (!aircraftList || aircraftList.length === 0) return;

        if (tabKey === 'timeseries' && (force || !lazyLoaded.bhFhTimeSeries)) {
            const regs = aircraftList.map(a => a.aircraft_reg).filter(Boolean);
            await fetchBhFhTimeSeriesData(regs.length > 0 ? regs : undefined);
            markLazyLoaded('bhFhTimeSeries');
        }
        if (tabKey === 'monthly' && (force || !lazyLoaded.monthlySeries)) {
            await fetchMonthlySeriesData(aircraftList);
            markLazyLoaded('monthlySeries');
        }
        if (tabKey === 'yearly' && (force || !lazyLoaded.yearlySeries)) {
            await fetchYearlySeriesData(aircraftList);
            markLazyLoaded('yearlySeries');
        }
        if (tabKey === 'leg_stats' && (force || !lazyLoaded.legStats)) {
            await fetchLegStatsData(aircraftList);
            markLazyLoaded('legStats');
        }
        if (tabKey === 'taxi_time' && (force || !lazyLoaded.taxiTimeSeries)) {
            await fetchTaxiTimeSeriesData(aircraftList);
            markLazyLoaded('taxiTimeSeries');
        }
    };

    const handleTopTabChange = async (key) => {
        setSearchParams({ tab: key });
        if (key === 'bh_fh' && dashboardData?.bh_fh && !lazyLoaded.aggregatedMetrics) {
            const aircraftList = await loadBhFhBaseData(dashboardData);
            await loadBhFhChartData(bhFhChartTab, aircraftList);
        }
    };

    const handleBhFhChartTabChange = async (key) => {
        setBhFhChartTab(key);
        let aircraftList = bhFhAircraftList;
        if ((!aircraftList || aircraftList.length === 0) && dashboardData?.bh_fh) {
            aircraftList = await loadBhFhBaseData(dashboardData);
        }
        await loadBhFhChartData(key, aircraftList);
    };

    const fetchDashboardStatistics = async () => {
        setLoading(true);
        setRouteDetailMap({});
        setAppliedDateRange(filters.date_range || null);
        resetLazyData({ clearSummaryData: false });
        try {
            const params = { complete_only: filters.complete_only };
            if (filters.aircraft_reg && filters.aircraft_reg.length > 0) {
                params.aircraft_reg = filters.aircraft_reg;
            }
            if (filters.date_range && filters.date_range.length === 2) {
                params.start_date = filters.date_range[0];
                params.end_date = filters.date_range[1];
            }

            // 1. 核算统计数据（独立try-catch，失败不阻断BH/FH数据加载）
            let data = {};
            try {
                const response = await settlementApi.routeInstances.getDashboardStatistics(params);
                data = response.data.data || {};
            } catch (err) {
                console.error('获取核算统计数据失败（不影响BH/FH统计）:', err);
            }
            
            setDeicingStats(data.deicing || null);
            setBhFhStats(data.bh_fh || null);
            setAirportFeesStats(data.airport_fees || null);
            setDashboardData(data);
            
            // 2. BH/FH数据（独立数据源，不依赖核算数据）
            if (activeTab === 'bh_fh') {
                const aircraftList = await loadBhFhBaseData(data);
                if (bhFhChartTab !== 'timeseries') {
                    await loadBhFhChartData(bhFhChartTab, aircraftList, true);
                }
            }
        } catch (error) {
            console.error('获取统计数据失败:', error);
            message.error('加载数据失败');
        } finally {
            setLoading(false);
        }
    };

    // H8: 判断是否跨月，动态切换列标题前缀
    const isCrossMonth = useMemo(() => {
        if (!filters.date_range || filters.date_range.length !== 2) return false;
        const start = dayjs(filters.date_range[0]);
        const end = dayjs(filters.date_range[1]);
        return !start.isSame(end, 'month');
    }, [filters.date_range]);

    const periodPrefix = isCrossMonth ? '期间' : '月';

    // 月度BH/FH Tab：跟随筛选器覆盖的起止自然月
    const monthlyBhFhRangeLabel = useMemo(() => {
        if (!filters.date_range || filters.date_range.length !== 2) return '';
        const start = dayjs(filters.date_range[0]);
        const end = dayjs(filters.date_range[1]);
        const startLabel = start.format('YYYY-MM');
        const endLabel = end.format('YYYY-MM');
        return startLabel === endLabel ? startLabel : `${startLabel} ~ ${endLabel}`;
    }, [filters.date_range]);

    const summaryTableColumns = [
        {
            title: '飞机注册号', dataIndex: 'aircraftReg', key: 'aircraftReg',
            width: 120, fixed: 'left', align: 'center',
            render: (text) => <Text strong style={{ fontSize: 13, color: '#1890ff' }}>{text}</Text>
        },
        {
            title: 'BH日均 (h)', dataIndex: 'bhDailyAvg', key: 'bhDailyAvg',
            width: 100, align: 'right',
            render: (text) => <Text style={{ color: '#52c41a', fontSize: 13 }}>{text}</Text>
        },
        {
            title: `${periodPrefix}总BH (h)`, dataIndex: 'monthTotalBh', key: 'monthTotalBh',
            width: 110, align: 'right',
            render: (text) => <Text strong style={{ color: '#1890ff', fontSize: 13 }}>{text}</Text>
        },
        {
            title: `${periodPrefix}总FH (h)`, dataIndex: 'monthTotalFh', key: 'monthTotalFh',
            width: 110, align: 'right',
            render: (text) => <Text strong style={{ color: '#ff7a45', fontSize: 13 }}>{text}</Text>
        },
        {
            title: '总航段数', dataIndex: 'monthTotalSegments', key: 'monthTotalSegments',
            width: 90, align: 'center',
            render: (text) => <Text style={{ color: '#8c8c8c', fontSize: 13 }}>{text} 段</Text>
        },
        {
            title: `${isCrossMonth ? '期间' : '月度'}小时循环比`, dataIndex: 'monthFhPerCycle', key: 'monthFhPerCycle',
            width: 120, align: 'right',
            render: (text) => <Text style={{ color: '#ff7a45', fontSize: 13 }}>{text}</Text>
        },
        {
            title: '年度总BH (h)', dataIndex: 'yearTotalBh', key: 'yearTotalBh',
            width: 110, align: 'right',
            render: (text) => <Text strong style={{ color: '#722ed1', fontSize: 13 }}>{text}</Text>
        },
        {
            title: '年度总FH (h)', dataIndex: 'yearTotalFh', key: 'yearTotalFh',
            width: 110, align: 'right',
            render: (text) => <Text strong style={{ color: '#eb2f96', fontSize: 13 }}>{text}</Text>
        },
        {
            title: '年度小时循环比', dataIndex: 'yearFhPerCycle', key: 'yearFhPerCycle',
            width: 110, align: 'right',
            render: (text) => <Text style={{ color: '#eb2f96', fontSize: 13 }}>{text}</Text>
        },
        {
            title: '年度月均BH (h)', dataIndex: 'yearMonthlyAvgBh', key: 'yearMonthlyAvgBh',
            width: 120, align: 'right',
            render: (text) => <Text style={{ color: '#13c2c2', fontSize: 13 }}>{text}</Text>
        }
    ];

    const summaryTableData = useMemo(() => {
        return Object.keys(aggregatedMetrics).map(reg => {
            const metrics = aggregatedMetrics[reg];
            if (!metrics?.monthly || !metrics?.yearly) return null;
            const m = metrics.monthly;
            const y = metrics.yearly;
            return {
                key: reg,
                aircraftReg: reg,
                bhDailyAvg: (m.bh_daily_avg ?? 0).toFixed(2),
                monthTotalBh: (m.total_bh ?? 0).toFixed(2),
                monthTotalFh: (m.total_fh ?? 0).toFixed(2),
                monthTotalSegments: m.total_cycles ?? 0,
                monthFhPerCycle: m.fh_per_cycle != null ? m.fh_per_cycle.toFixed(2) : '—',
                yearTotalBh: (y.total_bh ?? 0).toFixed(2),
                yearTotalFh: (y.total_fh ?? 0).toFixed(2),
                yearTotalSegments: y.total_cycles ?? 0,
                yearFhPerCycle: y.fh_per_cycle != null ? y.fh_per_cycle.toFixed(2) : '—',
                yearMonthlyAvgBh: (y.monthly_avg_bh ?? 0).toFixed(2)
            };
        }).filter(Boolean).sort((a, b) => a.aircraftReg.localeCompare(b.aircraftReg));
    }, [aggregatedMetrics]);

    // 构建Tabs的items配置
    const getTabItems = () => {
        const items = [];

        // 1. BH/FH 飞行小时统计（权限检查：statistics:bh_fh）
        if (hasPermission('statistics:bh_fh')) {
            items.push({
                key: 'bh_fh',
                label: <span style={{ fontSize: '14px' }}><span style={{ marginRight: '6px' }}>✈️</span>飞行小时</span>,
                children: (
                    <div style={{ padding: '16px' }}>
                        {/* 核心指标表格（10列，受筛选器影响） */}
                        <Card 
                            title={<span style={{ fontSize: '14px', fontWeight: '500' }}><span style={{ marginRight: '4px' }}>📊</span>核心指标</span>} 
                            size="small" 
                            style={{ borderRadius: '8px', boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)', marginBottom: '16px' }}
                        >
                            <Spin spinning={aggregatedMetricsLoading}>
                                <Table
                                    columns={summaryTableColumns}
                                    dataSource={summaryTableData}
                                    pagination={false}
                                    size="small"
                                    bordered
                                    rowKey="aircraftReg"
                                    scroll={{ x: 'max-content' }}
                                    style={{ background: '#fff', borderRadius: '4px' }}
                                    locale={{ emptyText: <Empty description="暂无指标数据" /> }}
                                />
                            </Spin>
                        </Card>

                        {/* 按航线统计平均BH（独立数据源，核心指标表格下方、图表子Tab上方） */}
                        <Card title={<span style={{ fontSize: '14px', fontWeight: '500' }}><span style={{ marginRight: '4px' }}>🛤️</span>按航线统计平均BH</span>} size="small" style={{ borderRadius: '8px', boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)', marginBottom: '16px' }}>
                            <Spin spinning={routeAvgLoading}>
                                <Table
                                    dataSource={routeAvgData}
                                    rowKey={(record, index) => `${record.aircraft_reg}_${record.route_code}_${index}`}
                                    pagination={false}
                                    size="small"
                                    bordered
                                    scroll={{ x: 'max-content' }}
                                    style={{ borderRadius: '4px', overflow: 'hidden' }}
                                    locale={{ emptyText: <Empty description="暂无航线数据" /> }}
                                    expandable={{
                                        expandedRowRender: renderRouteAvgExpandedRow,
                                        onExpand: handleRouteAvgExpand,
                                        rowExpandable: (record) => record.route_instance_count > 0,
                                    }}
                                    columns={[
                                        { title: '飞机注册号', dataIndex: 'aircraft_reg', width: 140, fixed: 'left', align: 'center', render: (text) => <Text strong style={{ fontSize: '14px', color: '#1890ff' }}>{text}</Text> },
                                        { title: '航线代码', dataIndex: 'route_code', width: 130, align: 'center', render: (text) => <Text style={{ fontSize: '13px' }}>{text}</Text> },
                                        { title: '航线航段数', dataIndex: 'route_legs', width: 110, align: 'center', render: (legs) => <Text style={{ color: '#8c8c8c' }}>{legs} 段</Text> },
                                        { title: '飞行次数', dataIndex: 'route_instance_count', width: 100, align: 'center', render: (count) => <Text>{count} 次</Text> },
                                        { title: '平均BH (h)', dataIndex: 'avg_bh', width: 120, align: 'right', render: (avgBh) => <Text strong style={{ color: '#722ed1', fontSize: '14px' }}>{avgBh?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text> }
                                    ]}
                                />
                            </Spin>
                        </Card>

                        {/* 图表区域（内部4个子Tab） */}
                        <Card 
                            size="small" 
                            style={{ borderRadius: '8px', boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)', marginBottom: '16px' }}
                            styles={{ body: { padding: '8px 16px 16px' } }}
                        >
                            <Tabs
                                activeKey={bhFhChartTab}
                                onChange={handleBhFhChartTabChange}
                                size="small"
                                destroyOnHidden
                                items={[
                                    {
                                        key: 'timeseries',
                                        label: '时间序列图',
                                        children: (
                                            <div>
                                                <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '12px', padding: '8px 12px', background: '#e6f7ff', borderLeft: '3px solid #1890ff', borderRadius: '4px' }}>
                                                    💡 横向展示各飞机每日平均 BH/FH 时间趋势（当日总飞行小时 ÷ 当日航段数），受页面日期筛选器影响
                                                </div>
                                                <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', fontSize: '12px', padding: '8px 12px', background: '#f9f9f9', borderRadius: '4px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><div style={{ width: '12px', height: '12px', background: '#fa8c16', borderRadius: '2px' }} /><span>平均 FH / 航段</span></div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><div style={{ width: '12px', height: '12px', background: '#1890ff', borderRadius: '2px' }} /><span>平均 BH / 航段</span></div>
                                                </div>
                                                {bhFhTimeSeriesLoading ? (
                                                    <div style={{ textAlign: 'center', padding: '40px' }}><Spin tip="加载图表数据中..." /></div>
                                                ) : Object.keys(bhFhTimeSeriesData).length > 0 ? (
                                                    <div>{Object.keys(bhFhTimeSeriesData).sort().map(reg => (
                                                        <BhFhChartRow key={reg} aircraftReg={reg} data={bhFhTimeSeriesData[reg]} loading={!bhFhTimeSeriesData[reg]} />
                                                    ))}</div>
                                                ) : <Empty description="暂无BH/FH数据" />}
                                            </div>
                                        )
                                    },
                                    {
                                        key: 'monthly',
                                        label: monthlyBhFhRangeLabel
                                            ? `月度BH/FH (${monthlyBhFhRangeLabel})`
                                            : '月度BH/FH',
                                        children: (
                                            <div>
                                                <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '12px', padding: '8px 12px', background: '#fffbe6', borderLeft: '3px solid #faad14', borderRadius: '4px' }}>
                                                    💡 展示日期筛选器覆盖的各自然月 BH/FH 整月汇总（选到月中某日仍按整月；可跨年），受页面日期筛选器影响
                                                </div>
                                                <MonthlyBhFhChart data={monthlySeriesData} loading={monthlySeriesLoading} />
                                            </div>
                                        )
                                    },
                                    {
                                        key: 'yearly',
                                        label: '年度BH/FH',
                                        children: (
                                            <div>
                                                <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '12px', padding: '8px 12px', background: '#f6ffed', borderLeft: '3px solid #52c41a', borderRadius: '4px' }}>
                                                    💡 展示各飞机历史年度BH/FH汇总，不受页面日期筛选器影响
                                                </div>
                                                <YearlyBhFhChart data={yearlySeriesData} loading={yearlySeriesLoading} />
                                            </div>
                                        )
                                    },
                                    {
                                        key: 'leg_stats',
                                        label: '航段统计',
                                        children: (
                                            <div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                                    <div style={{ fontSize: '12px', color: '#8c8c8c', padding: '8px 12px', background: '#f9f0ff', borderLeft: '3px solid #722ed1', borderRadius: '4px', flex: 1 }}>
                                                        💡 按飞机+航段聚合，展示平均BH/FH，受页面日期筛选器影响
                                                    </div>
                                                    <Button
                                                        icon={<ExportOutlined />}
                                                        size="small"
                                                        style={{ marginLeft: 12 }}
                                                        disabled={!legStatsData || legStatsData.length === 0}
                                                        onClick={() => {
                                                            const rows = legStatsData.map(r => ({
                                                                '飞机注册号': r.aircraft_reg,
                                                                '起飞机场': r.departure_airport,
                                                                '到达机场': r.arrival_airport,
                                                                '飞行次数': r.flight_count,
                                                                '平均BH (h)': r.avg_bh,
                                                                '平均FH (h)': r.avg_fh,
                                                            }));
                                                            const dateRange = filters.date_range || [];
                                                            exportDataToExcel(rows, '航段统计', `航段统计_${dateRange[0] || ''}_${dateRange[1] || ''}.xlsx`);
                                                            message.success('导出成功');
                                                        }}
                                                    >导出</Button>
                                                </div>
                                                <Spin spinning={legStatsLoading}>
                                                    <Table
                                                        dataSource={legStatsData}
                                                        rowKey={(record) => `${record.aircraft_reg}_${record.departure_airport}_${record.arrival_airport}`}
                                                        pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `共 ${total} 条` }}
                                                        size="small"
                                                        bordered
                                                        scroll={{ x: 'max-content' }}
                                                        locale={{ emptyText: <Empty description="暂无航段数据" /> }}
                                                        columns={[
                                                            { title: '飞机注册号', dataIndex: 'aircraft_reg', width: 120, fixed: 'left', align: 'center', render: (text) => <Text strong style={{ color: '#1890ff' }}>{text}</Text> },
                                                            { title: '航段', width: 150, align: 'center', render: (_, record) => <Text style={{ fontSize: '13px' }}>{record.departure_airport} → {record.arrival_airport}</Text> },
                                                            { title: '飞行次数', dataIndex: 'flight_count', width: 100, align: 'center', sorter: (a, b) => a.flight_count - b.flight_count, render: (count) => <Text>{count} 次</Text> },
                                                            { title: '平均BH (h)', dataIndex: 'avg_bh', width: 120, align: 'right', sorter: (a, b) => a.avg_bh - b.avg_bh, render: (val) => <Text strong style={{ color: '#1890ff' }}>{val?.toFixed(2)}</Text> },
                                                            { title: '平均FH (h)', dataIndex: 'avg_fh', width: 120, align: 'right', sorter: (a, b) => a.avg_fh - b.avg_fh, render: (val) => <Text strong style={{ color: '#52c41a' }}>{val?.toFixed(2)}</Text> }
                                                        ]}
                                                    />
                                                </Spin>
                                            </div>
                                        )
                                    },
                                    {
                                        key: 'taxi_time',
                                        label: '滑行时间统计',
                                        children: (
                                            <div>
                                                <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '12px', padding: '8px 12px', background: '#fff7e6', borderLeft: '3px solid #fa8c16', borderRadius: '4px' }}>
                                                    按机场维度展示平均滑行时间趋势。Taxi Out 归入出发机场，Taxi In 归入到达机场。受页面日期筛选器影响。
                                                    聚合粒度：{(() => { const g = calculateGranularity(filters.date_range?.[0], filters.date_range?.[1]); return g === 'month' ? '按月' : g === 'week' ? '按周' : '按日'; })()}
                                                </div>
                                                <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', fontSize: '12px', padding: '8px 12px', background: '#f9f9f9', borderRadius: '4px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><div style={{ width: '12px', height: '12px', background: '#fa8c16', borderRadius: '2px' }} /><span>Taxi Out（离场滑行）</span></div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><div style={{ width: '12px', height: '12px', background: '#52c41a', borderRadius: '2px' }} /><span>Taxi In（进场滑行）</span></div>
                                                </div>
                                                {taxiTimeSeriesLoading ? (
                                                    <div style={{ textAlign: 'center', padding: '40px' }}><Spin tip="加载滑行时间数据中..." /></div>
                                                ) : Object.keys(taxiTimeSeriesData).length > 0 ? (
                                                    <div>{Object.keys(taxiTimeSeriesData)
                                                        .sort((a, b) => {
                                                            const sumA = (taxiTimeSeriesData[a].leg_counts || []).reduce((s, v) => s + v, 0);
                                                            const sumB = (taxiTimeSeriesData[b].leg_counts || []).reduce((s, v) => s + v, 0);
                                                            return sumB - sumA;
                                                        })
                                                        .map(airport => (
                                                        <TaxiChartRow key={airport} label={airport} data={taxiTimeSeriesData[airport]} loading={!taxiTimeSeriesData[airport]} />
                                                    ))}</div>
                                                ) : <Empty description="暂无滑行时间数据" />}
                                            </div>
                                        )
                                    }
                                ]}
                            />
                        </Card>
                    </div>
                )
            });
        }

        // 2. 燃油统计
        if (hasPermission('statistics:fuel')) {
            items.push({
                key: 'fuel',
                label: <span style={{ fontSize: '14px' }}><span style={{ marginRight: '6px' }}>⛽</span>燃油统计</span>,
                children: <FuelCoveragePanel filters={filters} active={activeTab === 'fuel'} />,
            });
        }

        // 2.5 机场燃油价格（周度参考油价，矩阵展示 + 维护 + Excel 导入）
        // 专用权限，与「燃油统计」解耦：statistics:airport_fuel_price（查看）/ :edit（维护）
        if (hasPermission('statistics:airport_fuel_price')) {
            items.push({
                key: 'airport_fuel_price',
                label: <span style={{ fontSize: '14px' }}><span style={{ marginRight: '6px' }}>🛢️</span>机场燃油价格</span>,
                children: (
                    <AirportFuelPricePanel
                        active={activeTab === 'airport_fuel_price'}
                        canEdit={hasPermission('statistics:airport_fuel_price:edit')}
                    />
                ),
            });
        }

        // 3. 除冰费用统计（权限检查：statistics:deicing）
        if (deicingStats && hasPermission('statistics:deicing')) {
            items.push({
                key: 'deicing',
                label: <span style={{ fontSize: '14px' }}><span style={{ marginRight: '6px' }}>❄️</span>除冰费用</span>,
                children: (
                    <div style={{ padding: '16px' }}>
                        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                            <Col xs={24} sm={8}><Card hoverable style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}><Statistic title={<Space><DollarOutlined style={{ color: '#1890ff' }} /><span>平均除冰费用</span></Space>} value={deicingStats.overview.avg_deicing_cost} precision={2} valueStyle={{ color: '#1890ff', fontSize: '28px' }} prefix="$" suffix="USD" /></Card></Col>
                            <Col xs={24} sm={8}><Card hoverable style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}><Statistic title={<Space><NumberOutlined style={{ color: '#52c41a' }} /><span>除冰次数</span></Space>} value={deicingStats.overview.deicing_count} valueStyle={{ color: '#52c41a', fontSize: '28px' }} suffix="次" /></Card></Col>
                            <Col xs={24} sm={8}><Card hoverable style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}><Tooltip title="除冰费用占有除冰费用的航段总成本的比例"><Statistic title={<Space><PercentageOutlined style={{ color: '#faad14' }} /><span>占总成本比例</span></Space>} value={deicingStats.overview.deicing_percentage} precision={2} valueStyle={{ color: '#faad14', fontSize: '28px' }} suffix="%" /></Tooltip></Card></Col>
                        </Row>

                        <Row gutter={[16, 16]}>
                            <Col xs={24} lg={12}>
                                <Card title={<span style={{ fontSize: '14px', fontWeight: '500' }}><span style={{ marginRight: '4px' }}>🏢</span>机场排行榜 (Top 10 按平均值)</span>} size="small" style={{ borderRadius: '8px', boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)' }}>
                                    {deicingStats.by_airport?.length > 0 ? <Table dataSource={deicingStats.by_airport} rowKey="airport_code" pagination={false} size="small" bordered style={{ borderRadius: '4px', overflow: 'hidden' }} columns={[
                                        { title: '排名', key: 'rank', width: 80, align: 'center', render: (_, __, index) => <Tag color={index === 0 ? 'red' : index === 1 ? 'orange' : index === 2 ? 'gold' : 'default'}>{index + 1}</Tag> },
                                        { title: '机场代码', dataIndex: 'airport_code', width: 120, align: 'center', render: (code) => <Text strong style={{ fontSize: '14px' }}>{code}</Text> },
                                        { title: '除冰次数', dataIndex: 'count', width: 100, align: 'center', render: (count) => <Text>{count} 次</Text> },
                                        { title: '平均费用 (USD)', dataIndex: 'avg_cost', align: 'right', render: (cost) => <Text strong style={{ color: '#1890ff', fontSize: '14px' }}>${cost.toLocaleString('en-US', { minimumFractionDigits: 2 })}</Text> }
                                    ]} /> : <Empty description="暂无机场数据" />}
                                </Card>
                            </Col>
                            <Col xs={24} lg={12}>
                                <Card title={<Space><span style={{ fontSize: '14px', fontWeight: '500' }}><span style={{ marginRight: '4px' }}>✈️</span>机型/飞机对比</span><Tooltip title="优先显示机型，如机型为空则显示飞机注册号"><span style={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.45)', cursor: 'help' }}>ℹ️</span></Tooltip></Space>} size="small" style={{ borderRadius: '8px', boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)' }}>
                                    {deicingStats.by_aircraft_type?.length > 0 ? <Table dataSource={deicingStats.by_aircraft_type} rowKey="aircraft_type" pagination={false} size="small" bordered style={{ borderRadius: '4px', overflow: 'hidden' }} columns={[
                                        { title: '机型/飞机', dataIndex: 'aircraft_type', width: 160, align: 'center', render: (type) => <Text strong style={{ fontSize: '14px' }}>{type}</Text> },
                                        { title: '除冰次数', dataIndex: 'count', width: 100, align: 'center', render: (count) => <Text>{count} 次</Text> },
                                        { title: '平均费用 (USD)', dataIndex: 'avg_cost', align: 'right', render: (cost) => <Text strong style={{ color: '#1890ff', fontSize: '14px' }}>${cost.toLocaleString('en-US', { minimumFractionDigits: 2 })}</Text> }
                                    ]} /> : <Empty description={<div><div>暂无机型数据</div><div style={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.45)', marginTop: '8px' }}>可能原因：航班记录中未填写机型信息</div></div>} />}
                                </Card>
                            </Col>
                        </Row>
                    </div>
                )
            });
        }

        // 4. 地面服务费统计（权限检查：statistics:ground_handling）
        if (airportFeesStats?.ground_handling && hasPermission('statistics:ground_handling')) {
            items.push({
                key: 'ground_handling',
                label: <span style={{ fontSize: '14px' }}><span style={{ marginRight: '6px' }}>🚛</span>地面服务费</span>,
                children: <AirportFeesPanel data={airportFeesStats.ground_handling} title="地面服务费" icon="🚛" color="#13c2c2" countLabel="服务次数" tooltipText="地面服务费占总成本的比例" />
            });
        }

        // 5. 起降停场费统计（权限检查：statistics:landing_parking）
        if (airportFeesStats?.landing_parking && hasPermission('statistics:landing_parking')) {
            items.push({
                key: 'landing_parking',
                label: <span style={{ fontSize: '14px' }}><span style={{ marginRight: '6px' }}>🛬</span>起降停场费</span>,
                children: <AirportFeesPanel data={airportFeesStats.landing_parking} title="起降停场费" icon="🛬" color="#722ed1" countLabel="起降次数" tooltipText="起降停场费占总成本的比例" />
            });
        }

        // 6. 导航费统计（权限检查：statistics:navigation）
        if (airportFeesStats?.navigation && hasPermission('statistics:navigation')) {
            items.push({
                key: 'navigation',
                label: <span style={{ fontSize: '14px' }}><span style={{ marginRight: '6px' }}>🧭</span>导航费</span>,
                children: <AirportFeesPanel data={airportFeesStats.navigation} title="导航费" icon="🧭" color="#eb2f96" countLabel="飞行次数" tooltipText="导航费占总成本的比例" />
            });
        }

        return items;
    };

    const topTabItems = getTabItems();
    const topTabKeys = topTabItems.map(item => item.key);
    const topTabKeySignature = topTabKeys.join('|');
    // ?tab= 为空 / 非法 / 无权限时回退到首个可见 TAB（保持既有权限回退语义）
    const resolvedActiveTab = topTabKeys.includes(activeTab) ? activeTab : (topTabKeys[0] || activeTab);

    // URL 与实际激活 TAB 不一致时用 replace 回写，刷新 / 分享链接后仍停在同一 TAB，且不污染历史栈
    useEffect(() => {
        const availableTabKeys = topTabKeySignature ? topTabKeySignature.split('|') : [];
        if (availableTabKeys.length > 0 && !availableTabKeys.includes(activeTab)) {
            setSearchParams({ tab: availableTabKeys[0] }, { replace: true });
        }
    }, [activeTab, topTabKeySignature, setSearchParams]);

    return (
        <div className="statistics-dashboard-container">
            {/* 页面标题 */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', padding: '24px 0 0 0' }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                        <div style={{ width: '4px', height: '24px', background: '#52c41a', borderRadius: '2px', marginRight: '12px' }} />
                        <h4 style={{ margin: 0, fontWeight: 800, color: '#1f1f1f', fontSize: '24px', letterSpacing: '0.5px', lineHeight: 1.1 }}>统计报表</h4>
                    </div>
                    <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: 1.6, paddingLeft: '16px' }}>运营报表、财务报表、大屏</div>
                </div>
                <Space>
                    <Button type="default" icon={<TableOutlined />} onClick={() => navigate('/settlement/profit-reports')}>查看详细报表</Button>
                    <Button type="primary" icon={<ReloadOutlined />} onClick={fetchDashboardStatistics} loading={loading}>刷新</Button>
                </Space>
            </div>

            {/* 筛选器 */}
            <Card style={{ marginBottom: '16px' }}>
                <Space wrap size={[16, 16]}>
                    <div>
                        <Text type="secondary" style={{ marginRight: 8 }}>飞机号：</Text>
                        <AircraftSelector value={filters.aircraft_reg} onChange={(value) => { setFilters(prev => ({ ...prev, aircraft_reg: value || [] })); resetLazyData({ clearSummaryData: false }); }} mode="multiple" style={{ minWidth: 200 }} placeholder="全部飞机" allowClear maxTagCount="responsive" />
                    </div>
                    <div>
                        <Text type="secondary" style={{ marginRight: 8 }}>日期范围：</Text>
                        <RangePicker
                            value={filters.date_range ? [dayjs(filters.date_range[0]), dayjs(filters.date_range[1])] : null}
                            onChange={(dates) => { setFilters(prev => ({
                                ...prev,
                                date_range: dates?.length === 2 ? [dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')] : null
                            })); resetLazyData({ clearSummaryData: false }); }}
                            format="YYYY-MM-DD"
                            style={{ width: 260 }}
                        />
                    </div>
                    <Button type="primary" onClick={fetchDashboardStatistics} loading={loading}>应用筛选</Button>
                </Space>
            </Card>

            {/* 筛选条件提示 */}
            <div style={{ marginBottom: '20px', padding: '8px 12px', backgroundColor: (filters.aircraft_reg?.length > 0 || filters.date_range?.length > 0) ? '#e6f7ff' : '#f0f0f0', borderRadius: '4px', border: `1px solid ${(filters.aircraft_reg?.length > 0 || filters.date_range?.length > 0) ? '#91d5ff' : '#d9d9d9'}` }}>
                {(filters.aircraft_reg?.length > 0 || filters.date_range?.length > 0) ? (
                    <Space size={[8, 4]} wrap>
                        <Text type="secondary" style={{ fontSize: '12px' }}>📊 当前筛选：</Text>
                        {filters.aircraft_reg?.length > 0 && <Tag color="blue" style={{ fontSize: '12px' }}>飞机: {Array.isArray(filters.aircraft_reg) ? filters.aircraft_reg.join(', ') : filters.aircraft_reg}</Tag>}
                        {filters.date_range?.length === 2 && <Tag color="blue" style={{ fontSize: '12px' }}>日期: {filters.date_range[0]} ~ {filters.date_range[1]}</Tag>}
                        {filters.complete_only && <Tag color="green" style={{ fontSize: '12px' }}>核算统计：仅完整航线</Tag>}
                    </Space>
                ) : (
                    <Text type="secondary" style={{ fontSize: '12px' }}>💡 提示：显示所有数据。可使用上方筛选条件过滤数据</Text>
                )}
            </div>

            {/* 主内容 */}
            <Spin spinning={loading}>
                <Card className="data-card" style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}>
                    <Tabs 
                        activeKey={resolvedActiveTab} 
                        onChange={handleTopTabChange}
                        type="card" 
                        items={topTabItems}
                        tabBarGutter={16}
                        style={{ marginTop: '16px' }}
                        tabBarStyle={{ 
                            marginBottom: '16px',
                            paddingLeft: '8px'
                        }}
                    />
                </Card>
            </Spin>
        </div>
    );
};

export default StatisticsDashboardPage;
